import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  AlertTitle,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Autocomplete,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../AuthContext';
import api from '../api';
import axios from 'axios';

const DEV_API_BASE = 'https://dev-api.paradisetycoon.com';

const RARITY_MAP = {
  0: 'Common',
  1: 'Uncommon',
  2: 'Rare',
  3: 'Epic',
  4: 'Legendary'
};

const RARITY_COLORS = {
  0: '#9e9e9e',
  1: '#4caf50',
  2: '#2196f3',
  3: '#9c27b0',
  4: '#ff9800'
};

const BIOME_MAP = {
  0: 'Biome 1',
  1: 'Biome 2'
};

function DropTableTester() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingReference, setLoadingReference] = useState(true);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  // Reference data from API
  const [referenceData, setReferenceData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);

  // Form state with defaults
  const [formData, setFormData] = useState({
    islandTemplate: 'common1',
    islandBiome: 0,
    islandRarity: 0,
    resourceSpawnPointId: '',
    spawnPointType: 'Tree',
    collectedAssetId: '',
    toolAssetId: '',
    userModifiers: '[]',
    fertilizedCount: 0,
    scene: '',
    specialLocation: '',
    iterations: 100,
  });

  // Fetch reference data on mount
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const response = await axios.get(`${DEV_API_BASE}/test/dropTableReference`);
        setReferenceData(response.data);
        setLoadingReference(false);
      } catch (err) {
        console.error('Error fetching reference data:', err);
        setError('Failed to load reference data from API');
        setLoadingReference(false);
      }
    };

    fetchReferenceData();
  }, []);

  // Fetch assets from local API
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await api.get('/inventories/items');
        setAssets(response.data);
        setLoadingAssets(false);
      } catch (err) {
        console.error('Error fetching assets:', err);
        setLoadingAssets(false);
      }
    };

    fetchAssets();
  }, []);

  // Auto-select first spawn point and set rarity when template changes
  useEffect(() => {
    if (referenceData?.spawnPointsByTemplate && formData.islandTemplate) {
      const spawnPoints = referenceData.spawnPointsByTemplate[formData.islandTemplate];
      
      // Map template name to rarity
      const templateRarityMap = {
        'common1': 0,
        'uncommon1': 1,
        'rare1': 2,
        'epic1': 3,
        'legendary1': 4,
        'global': 0
      };
      
      const newRarity = templateRarityMap[formData.islandTemplate] ?? formData.islandRarity;
      
      if (spawnPoints && spawnPoints.length > 0) {
        const firstSpawnPoint = spawnPoints[0];
        setFormData(prev => ({
          ...prev,
          resourceSpawnPointId: firstSpawnPoint.resourceSpawnPointId,
          spawnPointType: firstSpawnPoint.type,
          islandRarity: newRarity,
        }));
      } else {
        // Even if no spawn points, update rarity
        setFormData(prev => ({
          ...prev,
          islandRarity: newRarity,
        }));
      }
    }
  }, [formData.islandTemplate, referenceData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSpawnPointChange = (spawnPointId) => {
    if (referenceData?.spawnPointsByTemplate && formData.islandTemplate) {
      const spawnPoints = referenceData.spawnPointsByTemplate[formData.islandTemplate];
      const selectedSpawnPoint = spawnPoints.find(sp => sp.resourceSpawnPointId === spawnPointId);
      
      if (selectedSpawnPoint) {
        setFormData(prev => ({
          ...prev,
          resourceSpawnPointId: selectedSpawnPoint.resourceSpawnPointId,
          spawnPointType: selectedSpawnPoint.type,
        }));
      }
    }
  };

  const handleClearForm = () => {
    setFormData({
      islandTemplate: 'common1',
      islandBiome: 0,
      islandRarity: 0,
      resourceSpawnPointId: '',
      spawnPointType: 'Tree',
      collectedAssetId: '',
      toolAssetId: '',
      userModifiers: '[]',
      fertilizedCount: 0,
      scene: '',
      specialLocation: '',
      iterations: 100,
    });
    setResults(null);
    setError(null);
  };

  const generateApiUrl = () => {
    const params = new URLSearchParams();
    
    // Required parameters
    params.set('islandTemplate', formData.islandTemplate);
    params.set('islandBiome', formData.islandBiome);
    params.set('islandRarity', formData.islandRarity);
    params.set('resourceSpawnPointId', formData.resourceSpawnPointId);
    params.set('spawnPointType', formData.spawnPointType);
    
    // Optional parameters
    if (formData.collectedAssetId) params.set('collectedAssetId', formData.collectedAssetId);
    if (formData.toolAssetId) params.set('toolAssetId', formData.toolAssetId);
    if (formData.userModifiers !== '[]') params.set('userModifiers', formData.userModifiers);
    if (formData.fertilizedCount > 0 && formData.spawnPointType === 'Farm') {
      params.set('fertilizedCount', formData.fertilizedCount);
    }
    if (formData.scene) params.set('scene', formData.scene);
    if (formData.specialLocation) params.set('specialLocation', formData.specialLocation);
    params.set('iterations', formData.iterations);

    return `${DEV_API_BASE}/test/dropTable?${params.toString()}`;
  };

  const copyApiUrl = async () => {
    const url = generateApiUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleRunTest = async () => {
    if (!formData.islandTemplate || !formData.resourceSpawnPointId) {
      setError('Island template and spawn point are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = generateApiUrl();
      const response = await axios.get(url);
      setResults(response.data);
    } catch (err) {
      console.error('Error testing drop table:', err);
      setError(err.response?.data?.message || err.message || 'Failed to test drop table');
    } finally {
      setLoading(false);
    }
  };

  // Get filtered tools based on spawn point type
  const getFilteredTools = () => {
    if (!assets.length) return [];
    
    const toolKeywords = {
      'Tree': ['axe', 'hatchet'],
      'Rock': ['pick', 'pickaxe'],
      'Farm': ['hoe', 'shovel'],
      'Fish': ['rod', 'fishing'],
    };

    const keywords = toolKeywords[formData.spawnPointType] || [];
    
    return assets.filter(asset => {
      const title = (asset.title || '').toLowerCase();
      return keywords.some(keyword => title.includes(keyword)) || 
             (asset.type && asset.type.toLowerCase().includes('tool'));
    });
  };

  // Get spawn points for current template
  const getCurrentSpawnPoints = () => {
    if (!referenceData?.spawnPointsByTemplate || !formData.islandTemplate) return [];
    return referenceData.spawnPointsByTemplate[formData.islandTemplate] || [];
  };

  // Check if user has manager or higher privileges
  if (!user || (user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'manager')) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">Access denied. Manager privileges or higher required.</Typography>
      </Box>
    );
  }

  if (loadingReference) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading reference data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        🎲 Drop Table Tester
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Query Builder */}
        <Box sx={{ flex: '0 0 30%', minWidth: 0 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Query Builder
                </Typography>
                <Tooltip title="Clear all fields">
                  <IconButton onClick={handleClearForm} size="small">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack spacing={2}>
                {/* Island Configuration */}
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                  Island Configuration
                </Typography>
                
                <FormControl fullWidth required>
                  <InputLabel>Island Template</InputLabel>
                  <Select
                    value={formData.islandTemplate}
                    onChange={(e) => handleInputChange('islandTemplate', e.target.value)}
                    label="Island Template"
                  >
                    {referenceData?.islandTemplates?.map(template => (
                      <MenuItem key={template} value={template}>
                        {template}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Island Biome</InputLabel>
                  <Select
                    value={formData.islandBiome}
                    onChange={(e) => handleInputChange('islandBiome', e.target.value)}
                    label="Island Biome"
                  >
                    {referenceData?.biomes && referenceData.biomes.length > 0 ? (
                      referenceData.biomes.map(biomeNum => (
                        <MenuItem key={biomeNum} value={biomeNum}>
                          {BIOME_MAP[biomeNum] || `Biome ${biomeNum}`}
                        </MenuItem>
                      ))
                    ) : (
                      <>
                        <MenuItem value={0}>Biome 0</MenuItem>
                        <MenuItem value={1}>Biome 1</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Island Rarity</InputLabel>
                  <Select
                    value={formData.islandRarity}
                    onChange={(e) => handleInputChange('islandRarity', e.target.value)}
                    label="Island Rarity"
                  >
                    {Object.entries(RARITY_MAP).map(([value, label]) => (
                      <MenuItem key={value} value={parseInt(value)}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={label}
                            size="small"
                            sx={{
                              bgcolor: RARITY_COLORS[value],
                              color: 'white',
                              fontSize: '0.7rem'
                            }}
                          />
                          <Typography variant="body2">({value})</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />

                {/* Resource Collection */}
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                  Resource Collection
                </Typography>

                <FormControl fullWidth required>
                  <InputLabel>Spawn Point</InputLabel>
                  <Select
                    value={formData.resourceSpawnPointId}
                    onChange={(e) => handleSpawnPointChange(e.target.value)}
                    label="Spawn Point"
                  >
                    {getCurrentSpawnPoints().map(sp => (
                      <MenuItem key={sp.resourceSpawnPointId} value={sp.resourceSpawnPointId}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight="bold">
                            {sp.resourceSpawnPointId}
                          </Typography>
                          <Chip label={sp.type} size="small" />
                          <Typography variant="caption" color="text.secondary">
                            ({sp.count}x)
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Spawn Point Type</InputLabel>
                  <Select
                    value={formData.spawnPointType}
                    onChange={(e) => handleInputChange('spawnPointType', e.target.value)}
                    label="Spawn Point Type"
                  >
                    {referenceData?.spawnPointTypes?.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {formData.spawnPointType === 'Farm' && (
                  <Autocomplete
                    options={assets}
                    getOptionLabel={(option) => `${option.title} (${option.id})`}
                    value={assets.find(a => a.id === formData.collectedAssetId) || null}
                    onChange={(e, newValue) => handleInputChange('collectedAssetId', newValue?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Collected Asset (Farm only)"
                        helperText="Optional: Specify what crop is being collected"
                      />
                    )}
                    loading={loadingAssets}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                {/* Optional Modifiers */}
                <Typography variant="subtitle2" color="secondary" sx={{ fontWeight: 'bold' }}>
                  Optional Modifiers
                </Typography>

                <Autocomplete
                  options={getFilteredTools()}
                  getOptionLabel={(option) => `${option.title} (${option.id})`}
                  value={assets.find(a => a.id === formData.toolAssetId) || null}
                  onChange={(e, newValue) => handleInputChange('toolAssetId', newValue?.id || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tool Asset"
                      helperText={`Tools for ${formData.spawnPointType}`}
                    />
                  )}
                  loading={loadingAssets}
                />

                {formData.spawnPointType === 'Farm' && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Fertilized Count"
                    value={formData.fertilizedCount}
                    onChange={(e) => handleInputChange('fertilizedCount', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 10 }}
                    helperText="Number of fertilizers applied (Farm only)"
                  />
                )}

                <TextField
                  fullWidth
                  label="User Modifiers (JSON)"
                  value={formData.userModifiers}
                  onChange={(e) => handleInputChange('userModifiers', e.target.value)}
                  placeholder='[]'
                  helperText="JSON array of user modifiers"
                  multiline
                  rows={2}
                />

                <FormControl fullWidth>
                  <InputLabel>Scene</InputLabel>
                  <Select
                    value={formData.scene}
                    onChange={(e) => handleInputChange('scene', e.target.value)}
                    label="Scene"
                  >
                    <MenuItem value="">None</MenuItem>
                    {referenceData?.scenes?.map(scene => (
                      <MenuItem key={scene} value={scene}>
                        {scene}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Special Location</InputLabel>
                  <Select
                    value={formData.specialLocation}
                    onChange={(e) => handleInputChange('specialLocation', e.target.value)}
                    label="Special Location"
                  >
                    <MenuItem value="">None</MenuItem>
                    {referenceData?.specialLocations?.map(location => (
                      <MenuItem key={location} value={location}>
                        {location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label="Iterations"
                  value={formData.iterations}
                  onChange={(e) => handleInputChange('iterations', parseInt(e.target.value) || 100)}
                  inputProps={{ min: 1, max: 10000 }}
                  helperText="Number of test runs (1-10000)"
                />

                <Divider sx={{ my: 2 }} />

                {/* Actions */}
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <PlayIcon />}
                    onClick={handleRunTest}
                    disabled={loading || !formData.resourceSpawnPointId}
                    fullWidth
                  >
                    {loading ? 'Running Test...' : 'Run Test'}
                  </Button>
                  <Tooltip title={copiedUrl ? 'Copied!' : 'Copy API URL'}>
                    <IconButton onClick={copyApiUrl} color={copiedUrl ? 'success' : 'default'}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {/* Generated URL Preview */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Generated API URL:
                  </Typography>
                  <a 
                    href={generateApiUrl()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#00e5ff', 
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    View API URL →
                  </a>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Results */}
        <Box sx={{ flex: '1 1 70%', minWidth: 0 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Results
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>Error</AlertTitle>
                  {error}
                </Alert>
              )}

              {loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Running drop table test...
                  </Typography>
                </Box>
              )}

              {results && !loading && (() => {
                // Process results to calculate summary statistics
                const iterations = results.iterations || formData.iterations;
                const allItems = {};
                const rarityCount = {};
                const dropTableStats = {};
                let totalDrops = 0;
                let echoCount = 0;
                let primeCropCount = 0;

                // Process results array
                if (results.results && Array.isArray(results.results)) {
                  results.results.forEach(result => {
                    // Track echo and prime crop
                    if (result.echoTriggered) echoCount++;
                    if (result.primeCrop) primeCropCount++;

                    // Process allItems from each iteration
                    if (result.allItems && Array.isArray(result.allItems)) {
                      result.allItems.forEach(item => {
                        const assetId = item.assetId;
                        const amount = item.amount || 1;
                        
                        // Handle both string and numeric rarities
                        let rarity = 0;
                        if (typeof item.rarity === 'string') {
                          const rarityMap = { 'common': 0, 'uncommon': 1, 'rare': 2, 'epic': 3, 'legendary': 4, 'unknown': 0 };
                          rarity = rarityMap[item.rarity.toLowerCase()] ?? 0;
                        } else {
                          rarity = item.rarity ?? 0;
                        }
                        
                        if (!allItems[assetId]) {
                          // Look up asset name from assets array
                          const asset = assets.find(a => a.id === assetId);
                          allItems[assetId] = {
                            assetId,
                            assetName: asset?.title || assetId,
                            timesDropped: 0,
                            totalAmount: 0,
                            rarity
                          };
                        }
                        
                        allItems[assetId].timesDropped++;
                        allItems[assetId].totalAmount += amount;
                        totalDrops++;
                        
                        rarityCount[rarity] = (rarityCount[rarity] || 0) + 1;
                      });
                    }

                    // Process dropTableSummary from each iteration
                    if (result.dropTableSummary && Array.isArray(result.dropTableSummary)) {
                      result.dropTableSummary.forEach(summary => {
                        const tableName = summary.dropTableName;
                        if (!dropTableStats[tableName]) {
                          dropTableStats[tableName] = {
                            timesTriggered: 0,
                            totalItems: 0,
                            totalAmount: 0,
                            rarities: {}
                          };
                        }
                        dropTableStats[tableName].timesTriggered++;
                        dropTableStats[tableName].totalItems += summary.itemsDropped || 0;
                        dropTableStats[tableName].totalAmount += summary.totalAmount || 0;
                        
                        // Aggregate rarities
                        if (summary.rarities) {
                          Object.entries(summary.rarities).forEach(([rarity, count]) => {
                            dropTableStats[tableName].rarities[rarity] = 
                              (dropTableStats[tableName].rarities[rarity] || 0) + count;
                          });
                        }
                      });
                    }
                  });
                }

                const itemBreakdown = Object.values(allItems).map(item => ({
                  ...item,
                  dropRate: item.timesDropped / iterations,
                  averageAmount: item.totalAmount / item.timesDropped
                })).sort((a, b) => b.timesDropped - a.timesDropped);

                const avgDropsPerRun = totalDrops / iterations;

                return (
                  <Stack spacing={2}>
                    {/* Summary */}
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(0,0,0,0.2)' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          📊 Test Summary
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Iterations
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                              {iterations}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Total Drops
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                              {totalDrops}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Per Run
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" color="primary">
                              {avgDropsPerRun.toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Unique Items
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                              {itemBreakdown.length}
                            </Typography>
                          </Grid>
                          {echoCount > 0 && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Echo Triggered
                              </Typography>
                              <Typography variant="h5" fontWeight="bold" color="secondary">
                                {echoCount} ({((echoCount / iterations) * 100).toFixed(1)}%)
                              </Typography>
                            </Grid>
                          )}
                          {primeCropCount > 0 && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Prime Crops
                              </Typography>
                              <Typography variant="h5" fontWeight="bold" color="success.main">
                                {primeCropCount} ({((primeCropCount / iterations) * 100).toFixed(1)}%)
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>

                    {/* Item Breakdown */}
                    {itemBreakdown.length > 0 && (
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">📦 Item Breakdown</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'rgba(0,0,0,0.2)' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Item</TableCell>
                                  <TableCell>Rarity</TableCell>
                                  <TableCell align="right">Times Dropped</TableCell>
                                  <TableCell align="right">Drop Rate</TableCell>
                                  <TableCell align="right">Total Qty</TableCell>
                                  <TableCell align="right">Avg Qty</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {itemBreakdown.map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight="bold">
                                        {item.assetName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                        {item.assetId}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={RARITY_MAP[item.rarity] || `R${item.rarity}`}
                                        sx={{
                                          bgcolor: RARITY_COLORS[item.rarity],
                                          color: 'white',
                                          fontSize: '0.7rem'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight="bold">
                                        {item.timesDropped}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip 
                                        label={`${(item.dropRate * 100).toFixed(1)}%`}
                                        size="small"
                                        color="primary"
                                      />
                                    </TableCell>
                                    <TableCell align="right">{item.totalAmount}</TableCell>
                                    <TableCell align="right">
                                      {item.averageAmount.toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Drop Table Summary */}
                    {Object.keys(dropTableStats).length > 0 && (
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">📊 Drop Table Breakdown</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'rgba(0,0,0,0.2)' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Drop Table</TableCell>
                                  <TableCell align="right">Times Triggered</TableCell>
                                  <TableCell align="right">Trigger Rate</TableCell>
                                  <TableCell align="right">Total Items</TableCell>
                                  <TableCell align="right">Total Amount</TableCell>
                                  <TableCell>Rarities</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(dropTableStats)
                                  .sort(([, a], [, b]) => b.timesTriggered - a.timesTriggered)
                                  .map(([tableName, stats]) => (
                                    <TableRow key={tableName}>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                          {tableName}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold">
                                          {stats.timesTriggered}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip 
                                          label={`${((stats.timesTriggered / iterations) * 100).toFixed(1)}%`}
                                          size="small"
                                          color="primary"
                                        />
                                      </TableCell>
                                      <TableCell align="right">{stats.totalItems}</TableCell>
                                      <TableCell align="right">{stats.totalAmount}</TableCell>
                                      <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                          {Object.entries(stats.rarities).map(([rarity, count]) => (
                                            <Chip
                                              key={rarity}
                                              label={`${rarity}: ${count}`}
                                              size="small"
                                              sx={{ 
                                                fontSize: '0.7rem',
                                                textTransform: 'capitalize'
                                              }}
                                            />
                                          ))}
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Rarity Breakdown */}
                    {Object.keys(rarityCount).length > 0 && (
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">🌟 Rarity Breakdown</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={2}>
                            {Object.entries(rarityCount).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([rarity, count]) => {
                              const percentage = (count / totalDrops) * 100;
                              return (
                                <Card key={rarity} variant="outlined" sx={{ bgcolor: 'rgba(0,0,0,0.2)' }}>
                                  <CardContent>
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                      <Chip
                                        label={RARITY_MAP[rarity] || `Rarity ${rarity}`}
                                        sx={{ 
                                          bgcolor: RARITY_COLORS[rarity],
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {count} drops ({percentage.toFixed(1)}%)
                                      </Typography>
                                    </Stack>
                                    <LinearProgress
                                      variant="determinate"
                                      value={percentage}
                                      sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                          bgcolor: RARITY_COLORS[rarity],
                                        }
                                      }}
                                    />
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Raw Response */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">📋 Raw Response</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ bgcolor: 'rgba(0,0,0,0.3)', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', maxHeight: '400px', overflow: 'auto' }}>
                          <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(results, null, 2)}
                          </pre>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                );
              })()}

              {!results && !loading && !error && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography variant="body1">
                    Configure your test parameters and click &quot;Run Test&quot; to see results
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

export default DropTableTester;
